// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides a form-integrated location search component.
 * It wraps the LocationSearch component with react-hook-form integration
 * for seamless form handling and validation.
 */
import {
  LocationSearch,
  LocationSearchValue,
} from '@/location/components/location-search';
import {
  CustomPriorityGroup,
  LocationGroupingMethod,
} from '@/location/components/location-search-options';
import { SparseLocation } from '@/sparse/api/types';
import { ReactNode } from 'react';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

/**
 * Props for the FormLocationSearch component.
 * Extends react-hook-form's controller props for form integration.
 */
interface Props<Values extends FieldValues, Multiple extends boolean = false>
  extends UseControllerProps<Values> {
  /**
   * If true, multiple locations can be selected.
   * When true, the value will be an array of locations.
   * When false, the value will be a single location or null.
   */
  multiple?: boolean;

  /**
   * Optional grouping configuration for location options.
   * Allows organizing locations into hierarchical categories for better navigation.
   *
   * @example
   * grouping={['inventory']} // Groups by inventory status
   * grouping={['inventory', 'customer']} // Groups by inventory, then customer within each inventory group
   */
  grouping?: LocationGroupingMethod[];

  /**
   * Optional custom sort function for location options.
   * If not provided, locations will be sorted alphabetically by name.
   *
   * @param location - First location to compare
   * @param other - Second location to compare
   * @returns Negative if location should come before other,
   *          positive if location should come after other,
   *          zero if they are equal
   */
  sort?: (location: SparseLocation, other: SparseLocation) => number;

  /**
   * Label text displayed above the search input.
   * Should be descriptive of what the field represents.
   */
  label: string;

  /**
   * Instance of react-hook-form Control.
   * Used to register the component with the form.
   */
  control: Control<Values>;

  /**
   * Callback fired when the user selects the "create location" option.
   * Receives the entered location name as a parameter.
   *
   * @param name - The name entered for the new location
   */
  onCreate: (name: string) => void;

  onChange?: (value: LocationSearchValue<Multiple>) => void;

  /**
   * Remap allows for a way to override the locations, example: decorating the name
   */
  remap?: (location: SparseLocation) => SparseLocation;

  /**
   * If provided, locations belonging to this customer will be moved to the top of the list.
   * This is useful for prioritizing a specific customer's locations in the dropdown.
   * Only effective when using customer grouping.
   * Note: This prop is ignored if customPriorityGroup is provided.
   */
  priorityCustomerId?: string;

  /**
   * If provided, creates a custom priority group that appears first in the dropdown.
   * Locations matching the IDs in this group will be removed from their original groups
   * and displayed together under the custom label. All non-priority locations will be
   * displayed with italic styling.
   * When set, this prop overrides the priorityCustomerId prop.
   */
  customPriorityGroup?: CustomPriorityGroup;

  /**
   * Optional adornment rendered before the selected location's address in
   * the helper row (e.g. an off-project warning icon).
   */
  helperAdornment?: ReactNode;

  /**
   * If provided, sets a minimum width for the dropdown popper.
   * Useful when the input field is narrow but options need more space.
   */
  popperWidth?: number;

  /**
   * If true, the selected value's helper row shows the zone name before the
   * address (for narrow inputs like the multi-move grid).
   */
  compact?: boolean;
}

/**
 * A controlled location search component that integrates with react-hook-form.
 *
 * Features:
 * - Seamless integration with react-hook-form
 * - Support for single or multiple location selection
 * - Optional grouping of location options
 * - Custom sorting capabilities
 * - Built-in form validation
 * - "Create new location" functionality
 *
 * Form Integration:
 * - Automatically handles form state
 * - Provides validation feedback
 * - Supports form reset and default values
 * - Maintains controlled component behavior
 *
 * @example
 * ```tsx
 * // Basic usage in a form
 * <FormLocationSearch
 *   name="location"
 *   label="Select location"
 *   control={form.control}
 *   onCreate={handleCreateLocation}
 * />
 *
 * // Multiple selection with grouping
 * <FormLocationSearch
 *   multiple
 *   name="locations"
 *   label="Select locations"
 *   control={form.control}
 *   grouping={['customer']}
 *   onCreate={handleCreateLocation}
 * />
 *
 * // Hierarchical grouping
 * <FormLocationSearch
 *   name="location"
 *   label="Select location"
 *   control={form.control}
 *   grouping={['inventory', 'customer']}
 *   onCreate={handleCreateLocation}
 * />
 * ```
 */
export function FormLocationSearch<
  Values extends FieldValues,
  Multiple extends boolean = false,
>(props: Props<Values, Multiple>) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // This component is always controlled, so an undefined value must be
  // coallesced to null or an empty array.
  const value = field.value ?? (props.multiple ? [] : null);

  return (
    <LocationSearch
      disabled={props.disabled}
      ref={field.ref}
      sort={props.sort}
      name={field.name}
      label={props.label}
      grouping={props.grouping}
      value={value}
      multiple={props.multiple}
      remap={props.remap}
      priorityCustomerId={props.priorityCustomerId}
      customPriorityGroup={props.customPriorityGroup}
      popperWidth={props.popperWidth}
      compact={props.compact}
      helperAdornment={props.helperAdornment}
      onChange={(value) => {
        field.onChange(value);
        if (props.onChange) {
          props.onChange(value as LocationSearchValue<Multiple>);
        }
      }}
      onCreate={props.onCreate}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
    />
  );
}
